import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const MindYourNature = () => {
  useEffect(() => {
    document.title = "Mind Your Nature | Under Open Sky";
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <nav className="p-8 sticky top-0 bg-white/80 backdrop-blur-md z-10">
        <Link to="/" className="text-[10px] uppercase tracking-[0.2em] text-gray-400 hover:text-gray-800 transition-colors">
          ← Zurück zur Übersicht
        </Link>
      </nav>

      <article className="max-w-2xl mx-auto px-6 py-12 pb-40 font-light text-gray-600 leading-relaxed">
        <header className="mb-20">
          <h1 className="text-3xl font-light text-gray-800 mb-4 tracking-tight">Mind Your Nature</h1>
          <p className="text-gray-400 italic">Eine nüchterne Erklärung</p>
        </header>
        
        <div className="space-y-16">
          <section>
            <h2 className="text-lg font-normal text-gray-800 mb-6">Ausgangspunkt</h2>
            <div className="space-y-4">
              <p>Menschen handeln selten aus freier Entscheidung. In den meisten Situationen reagieren sie automatisch. Diese Automatismen sind kein Fehler, sondern das Ergebnis von Evolution, Lernen und Gewöhnung.</p>
              <p>Das menschliche Gehirn ist darauf ausgelegt, Zusammenhänge herzustellen (Kohärenz), Energie zu sparen und schnell zu reagieren, wenn Gefahr vermutet wird.</p>
              <p>Diese Eigenschaften sichern Überleben – führen im heutigen Alltag jedoch häufig zu innerer Anspannung, unbemerkter Angst und reaktiven Verhaltensmustern.</p>
              <p className="italic pt-4 text-gray-500 border-t border-gray-50">Die Karte „Mind Your Nature“ ist kein Werkzeug zur Selbstoptimierung. Sie ist eine Erinnerung daran, diese Automatismen sichtbar zu machen und dort Wahlfreiheit zurückzugewinnen, wo sie verloren gegangen ist.</p>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-normal text-gray-800 mb-6">Kohärenzstreben</h2>
            <p>Das Gehirn strebt nach Kohärenz. Es versucht ständig, Erlebnisse, Gedanken und Gefühle zu einer stimmigen Geschichte zu verbinden. Dieses Kohärenzstreben reduziert Unsicherheit und gibt Orientierung.</p>
            <p className="mt-4">Der Preis dafür: Vereinfachung, Verzerrung und Festhalten an bekannten Mustern. Einmal gelernte Deutungen werden unbemerkt stabilisiert – auch dann, wenn sie zur aktuellen Situation nicht mehr passen. Angst entsteht dabei nicht zwingend aus realer Gefahr, sondern aus kohärenten, aber überholten inneren Erzählungen.</p>
          </section>

          <section>
            <h2 className="text-lg font-normal text-gray-800 mb-6">Energiesparen & Angst</h2>
            <p>Das Gehirn arbeitet bevorzugt im Energiesparmodus. Bewusstes Nachdenken, Hinterfragen und Umlernen kostet spürbar Energie. Deshalb greift das System bevorzugt auf Gewohnheiten zurück – selbst dann, wenn diese nicht hilfreich sind.</p>
            <p className="mt-4">Angst ist kein Defekt, sondern ein Schutzmechanismus. Problematisch wird sie, wenn sie dauerhaft aktiv bleibt oder nicht mehr zur realen Situation passt. Viele heutige Ängste sind erlernte Reaktionsmuster (Ablehnung, Kontrollverlust), die oft unterhalb der bewussten Wahrnehmung wirken.</p>
          </section>

          <section className="bg-gray-50 p-10 rounded-sm">
            <h2 className="text-lg font-normal text-gray-800 mb-6">Die Taschenkarte</h2>
            <p className="mb-8 text-sm text-gray-500 leading-relaxed">
              Die Karte lädt zu einer Unterbrechung ein: Pause machen, den Körper spüren und bemerken, welche Impulse gerade aktiv sind.
            </p>
            <div className="relative group overflow-hidden bg-white p-4 shadow-sm border border-gray-100">
              <img 
                src="https://github.com/mike3011/Bilder/blob/main/Vom_Reiz_zur_Reaktion.png?raw=true" 
                alt="Vom Reiz zur Reaktion Grafik" 
                className="w-full h-auto" 
              />
            </div>
            <div className="mt-8 flex items-center justify-between">
              <span className="text-xs text-gray-400 italic">Vom Reiz zur Reaktion</span>
              <a 
                href="https://github.com/mike3011/Bilder/blob/main/Vom_Reiz_zur_Reaktion.png?raw=true" 
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-6 py-3 bg-gray-800 text-white text-[10px] uppercase tracking-widest hover:bg-gray-600 transition-all"
              >
                Grafik öffnen & Speichern
              </a>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-normal text-gray-800 mb-6">Wahlfreiheit im Kleinen</h2>
            <p>Verstehen allein verändert wenig. Entscheidend ist die wiederholte Erfahrung: Ich hätte automatisch reagieren können, habe aber innegehalten. Diese Erfahrung erzeugt Selbstwirksamkeit.</p>
            <p className="mt-4 italic">Die Karte verspricht nichts. Sie lädt ein, Automatismen zu bemerken, Angst einzuordnen und Wahlfreiheit im Kleinen zu üben. Nicht dauerhaft. Nicht perfekt. Aber immer wieder.</p>
          </section>
        </div>
      </article>
    </div>
  );
};

export default MindYourNature;